import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, ChevronDown, Tag } from 'lucide-react';
import { useTaskCategories, useTaskCategoriesActions } from '../hooks';
import { CATEGORY_COLORS } from '../taskCategoriesStore';

interface CategoryMultiSelectProps {
  selectedCategoryIds: string[];
  onChange: (categoryIds: string[]) => void;
  allowInlineCreate?: boolean;
  compact?: boolean;
}

export function CategoryMultiSelect({
  selectedCategoryIds,
  onChange,
  allowInlineCreate = true,
  compact = false,
}: CategoryMultiSelectProps) {
  const { t } = useTranslation();
  const categories = useTaskCategories();
  const { addNewCategory } = useTaskCategoriesActions();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const createdRef = useRef(false);

  // Selected categories (resolved from IDs)
  const selectedCategories = categories.filter(c => selectedCategoryIds.includes(c.id));
  // Available (unselected) categories for dropdown
  const availableCategories = categories.filter(c => !selectedCategoryIds.includes(c.id));

  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus();
      createdRef.current = false;
    }
  }, [isCreating]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
        setIsCreating(false);
        setNewName('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isDropdownOpen]);

  const removeCategory = useCallback((categoryId: string) => {
    onChange(selectedCategoryIds.filter(id => id !== categoryId));
  }, [selectedCategoryIds, onChange]);

  const addCategory = useCallback((categoryId: string) => {
    onChange([...selectedCategoryIds, categoryId]);
  }, [selectedCategoryIds, onChange]);

  const handleCreate = useCallback(() => {
    if (createdRef.current) return;
    const name = newName.trim();
    if (!name) return;
    createdRef.current = true;
    const color = CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length];
    const newId = addNewCategory(name, color);
    onChange([...selectedCategoryIds, newId]);
    setNewName('');
    setIsCreating(false);
  }, [newName, categories.length, addNewCategory, selectedCategoryIds, onChange]);

  const handleCreateKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreate();
    } else if (e.key === 'Escape') {
      setNewName('');
      setIsCreating(false);
    }
  };

  const chipFontSize = compact ? 11 : 12;
  const gap = compact ? 4 : 6;

  return (
    <div style={{ display: 'flex', flexDirection: compact ? 'row' : 'column', gap: gap, alignItems: compact ? 'center' : undefined }}>
      {/* Selected categories with X buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap, alignItems: 'center' }}>
        {selectedCategories.map(cat => (
          <span
            key={cat.id}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              padding: compact ? '4px 4px 4px 8px' : '1px 6px 1px 8px',
              fontSize: compact ? 12 : chipFontSize,
              borderRadius: compact ? 6 : 9999,
              border: compact ? `1px solid ${cat.color}60` : `1px solid ${cat.color}80`,
              backgroundColor: cat.color + '15',
              color: cat.color,
              lineHeight: compact ? 'normal' : '20px',
              fontWeight: 500,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: cat.color,
                flexShrink: 0,
              }}
            />
            {cat.name}
            <button
              type="button"
              onClick={() => removeCategory(cat.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: 'none',
                backgroundColor: 'transparent',
                color: cat.color,
                cursor: 'pointer',
                borderRadius: '50%',
                width: compact ? 14 : 16,
                height: compact ? 14 : 16,
                opacity: 0.6,
                transition: 'opacity 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
              title={t('categorySelect.removeCategory', { name: cat.name })}
            >
              <X size={compact ? 10 : 12} />
            </button>
          </span>
        ))}

        {/* Add button (opens dropdown) */}
        <div style={{ position: 'relative' }} ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={compact ? {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid #e5e7eb',
              backgroundColor: isDropdownOpen ? '#f3f4f6' : '#f9fafb',
              color: '#374151',
              cursor: 'pointer',
              transition: 'all 0.15s',
              fontWeight: 500,
            } : {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              padding: '2px 8px',
              fontSize: chipFontSize,
              borderRadius: 9999,
              border: '1px dashed #d1d5db',
              backgroundColor: isDropdownOpen ? '#f3f4f6' : '#f9fafb',
              color: '#9ca3af',
              cursor: 'pointer',
              transition: 'all 0.15s',
              lineHeight: '20px',
            }}
            onMouseEnter={(e) => {
              if (!isDropdownOpen) {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                if (!compact) e.currentTarget.style.borderColor = '#9ca3af';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDropdownOpen) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                if (!compact) e.currentTarget.style.borderColor = '#d1d5db';
              }
            }}
          >
            {compact ? (
              <>
                <Tag size={12} style={{ color: '#9ca3af' }} />
                {selectedCategories.length === 0 ? t('categorySelect.categories') : null}
                <ChevronDown size={12} style={{ color: '#9ca3af' }} />
              </>
            ) : (
              <>
                <Plus size={12} />
                <ChevronDown size={10} style={{ marginLeft: -1 }} />
              </>
            )}
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 4,
                zIndex: 50,
                minWidth: 180,
                maxHeight: 220,
                overflowY: 'auto',
                backgroundColor: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              {availableCategories.length === 0 && !allowInlineCreate && (
                <div style={{ padding: '8px 12px', fontSize: 11, color: '#9ca3af' }}>
                  {t('categorySelect.noMore')}
                </div>
              )}

              {availableCategories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    addCategory(cat.id);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                    padding: '6px 12px',
                    fontSize: chipFontSize,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#374151',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.1s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: cat.color,
                      flexShrink: 0,
                    }}
                  />
                  {cat.name}
                </button>
              ))}

              {/* Inline create */}
              {allowInlineCreate && (
                <div style={{ borderTop: availableCategories.length > 0 ? '1px solid #f1f5f9' : 'none', padding: '6px 8px' }}>
                  {!isCreating ? (
                    <button
                      type="button"
                      onClick={() => setIsCreating(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        width: '100%',
                        padding: '4px 4px',
                        fontSize: chipFontSize,
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#9ca3af',
                        cursor: 'pointer',
                        borderRadius: 4,
                        transition: 'all 0.1s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#9ca3af';
                      }}
                    >
                      <Plus size={12} />
                      {t('categorySelect.createNew')}
                    </button>
                  ) : (
                    <input
                      ref={inputRef}
                      type="text"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={handleCreateKeyDown}
                      onBlur={() => {
                        if (newName.trim()) {
                          handleCreate();
                        } else {
                          setIsCreating(false);
                        }
                      }}
                      placeholder="Name..."
                      style={{
                        width: '100%',
                        padding: '4px 8px',
                        fontSize: chipFontSize,
                        borderRadius: 4,
                        border: '1px solid #3b82f6',
                        backgroundColor: '#fff',
                        color: '#1f2937',
                        outline: 'none',
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Empty state hint (hidden in compact mode) */}
      {!compact && selectedCategories.length === 0 && !isDropdownOpen && (
        <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
          {t('categorySelect.noneAssigned')}
        </div>
      )}
    </div>
  );
}
